package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*


import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.AbsenceRequestDto
import se.mittpass.shared.api.CreateAbsenceRequest
import se.mittpass.shared.api.ReviewAbsenceRequest

private val absenceScope = MainScope()

private val ABSENCE_TYPES = listOf(
    "sick" to "Sjuk",
    "vab" to "VAB",
    "vacation" to "Semester",
    "leave" to "Tjänstledig"
)

private val STATUS_LABELS = mapOf(
    "pending" to "Väntar",
    "approved" to "Godkänd",
    "rejected" to "Nekad"
)

@Composable
fun AbsencePage(workplaceId: Long, isManager: Boolean) {
    var absences by remember { mutableStateOf<List<AbsenceRequestDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var showCreateModal by remember { mutableStateOf(false) }
    var feedbackMsg by remember { mutableStateOf<String?>(null) }

    fun loadAbsences() {
        loading = true
        absenceScope.launch {
            absenceRepo.getAbsences(workplaceId).onSuccess { absences = it }
            loading = false
        }
    }

    LaunchedEffect(workplaceId) { loadAbsences() }

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        feedbackMsg?.let { msg ->
            Div({
                style {
                    property("background", if (msg.startsWith("\u2713")) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                    property("color", if (msg.startsWith("\u2713")) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                    property("padding", "0.75rem 1.25rem"); property("border-radius", "14px"); property("font-weight", "600")
                }
            }) { Text(msg) }
        }

        // Header with create button
        Div({
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.25rem")
                property("display", "flex"); property("justify-content", "space-between"); property("align-items", "center")
            }
        }) {
            H3({ style { property("margin", "0"); property("font-size", "1.3rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                Text("Frånvaro")
            }
            Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("align-items", "center") } }) {
                val pendingCount = absences.count { it.status == "pending" }
                if (pendingCount > 0 && isManager) {
                    Span({ style { property("background", "var(--rp-warn-bg)"); property("color", "var(--rp-gold)"); property("padding", "0.4rem 1rem"); property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.85rem") } }) {
                        Text("$pendingCount att granska")
                    }
                }
                Button({
                    onClick { showCreateModal = true }
                    style {
                        property("padding", "0.6rem 1.25rem"); property("border", "none")
                        property("background", "var(--rp-pine)"); property("color", "white")
                        property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                    }
                }) { Text("+ Ny frånvaro") }
            }
        }

        // Absences list
        Div({ style { property("background", "var(--rp-surface)"); property("border-radius", "24px"); property("padding", "1.5rem") } }) {
            if (loading) {
                Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-subtle)") } }) { Text("Laddar\u2026") }
            } else if (absences.isEmpty()) {
                Div({ style { property("text-align", "center"); property("padding", "2rem"); property("color", "var(--rp-muted)") } }) { Text("Inga frånvaroärenden.") }
            } else {
                // Header row
                Div({
                    style {
                        property("display", "grid"); property("grid-template-columns", if (isManager) "1fr 100px 100px 100px 80px 120px" else "100px 100px 100px 80px 100px")
                        property("gap", "0.5rem"); property("padding", "0.75rem 1rem")
                        property("background", "var(--rp-overlay)"); property("border-radius", "14px"); property("margin-bottom", "0.5rem")
                        property("font-weight", "700"); property("font-size", "0.7rem"); property("color", "var(--rp-subtle)")
                        property("text-transform", "uppercase"); property("letter-spacing", "0.5px")
                    }
                }) {
                    if (isManager) { Span { Text("Namn") } }
                    Span { Text("Typ") }; Span { Text("Från") }; Span { Text("Till") }
                    Span { Text("Status") }; Span { }
                }

                absences.forEach { absence ->
                    val name = listOfNotNull(absence.firstName, absence.lastName).joinToString(" ").ifEmpty { absence.userEmail }
                    val typeLabel = ABSENCE_TYPES.find { it.first == absence.type }?.second ?: absence.type
                    val statusLabel = STATUS_LABELS[absence.status] ?: absence.status

                    val (statusBg, statusColor) = when (absence.status) {
                        "approved" -> "var(--rp-ok-bg)" to "var(--rp-ok-text)"
                        "rejected" -> "var(--rp-err-bg)" to "var(--rp-err-text)"
                        else -> "var(--rp-warn-bg)" to "var(--rp-warn-text)"
                    }

                    Div({
                        style {
                            property("display", "grid"); property("grid-template-columns", if (isManager) "1fr 100px 100px 100px 80px 120px" else "100px 100px 100px 80px 100px")
                            property("gap", "0.5rem"); property("padding", "0.85rem 1rem"); property("align-items", "center")
                            property("border-bottom", "1px solid var(--rp-hl-med)")
                        }
                    }) {
                        if (isManager) {
                            Span({ style { property("font-weight", "600"); property("color", "var(--rp-text)"); property("font-size", "0.9rem") } }) { Text(name) }
                        }
                        Span({ style { property("font-weight", "600"); property("font-size", "0.85rem") } }) {
                            val (typeBg, typeColor) = when (absence.type) {
                                "sick" -> "var(--rp-err-bg)" to "var(--rp-err-text)"
                                "vab" -> "var(--rp-warn-bg)" to "var(--rp-warn-text)"
                                "vacation" -> "var(--rp-info-bg)" to "var(--rp-pine)"
                                else -> "var(--rp-overlay)" to "var(--rp-subtle)"
                            }
                            Span({ style { property("background", typeBg); property("color", typeColor); property("padding", "0.2rem 0.6rem"); property("border-radius", "50px"); property("font-size", "0.75rem"); property("font-weight", "700") } }) {
                                Text(typeLabel)
                            }
                        }
                        Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) { Text(absence.startDate) }
                        Span({ style { property("color", "var(--rp-subtle)"); property("font-size", "0.85rem") } }) { Text(absence.endDate) }
                        Span({
                            style {
                                property("font-weight", "700"); property("font-size", "0.75rem")
                                property("padding", "0.25rem 0.6rem"); property("border-radius", "50px"); property("text-align", "center")
                                property("background", statusBg); property("color", statusColor)
                            }
                        }) { Text(statusLabel) }
                        Div({ style { property("display", "flex"); property("gap", "0.3rem") } }) {
                            if (isManager && absence.status == "pending") {
                                Button({
                                    onClick {
                                        absenceScope.launch {
                                            absenceRepo.reviewAbsence(absence.id, ReviewAbsenceRequest("approved"))
                                            feedbackMsg = "\u2713 Godkänd"; loadAbsences()
                                        }
                                    }
                                    style { property("padding", "0.3rem 0.6rem"); property("border", "none"); property("background", "var(--rp-ok-bg)"); property("border-radius", "8px"); property("font-size", "0.75rem"); property("cursor", "pointer"); property("color", "var(--rp-pine)"); property("font-weight", "700") }
                                }) { Text("\u2713") }
                                Button({
                                    onClick {
                                        absenceScope.launch {
                                            absenceRepo.reviewAbsence(absence.id, ReviewAbsenceRequest("rejected"))
                                            feedbackMsg = "\u2713 Nekad"; loadAbsences()
                                        }
                                    }
                                    style { property("padding", "0.3rem 0.6rem"); property("border", "none"); property("background", "var(--rp-err-bg)"); property("border-radius", "8px"); property("font-size", "0.75rem"); property("cursor", "pointer"); property("color", "var(--rp-love)"); property("font-weight", "700") }
                                }) { Text("\u2717") }
                            }
                            if (!isManager && absence.status == "pending") {
                                Button({
                                    onClick {
                                        absenceScope.launch {
                                            absenceRepo.deleteAbsence(absence.id)
                                            feedbackMsg = "\u2713 Borttagen"; loadAbsences()
                                        }
                                    }
                                    style { property("padding", "0.3rem 0.6rem"); property("border", "none"); property("background", "var(--rp-overlay)"); property("border-radius", "8px"); property("font-size", "0.75rem"); property("cursor", "pointer"); property("color", "var(--rp-subtle)") }
                                }) { Text("Ta bort") }
                            }
                        }
                    }

                    absence.note?.let { note ->
                        if (note.isNotBlank()) {
                            Div({
                                style {
                                    property("padding", "0.3rem 1rem 0.6rem 1rem"); property("font-size", "0.8rem")
                                    property("color", "var(--rp-muted)"); property("font-style", "italic")
                                }
                            }) { Text(note) }
                        }
                    }
                }
            }
        }
    }

    // Create absence modal
    if (showCreateModal) {
        var selectedType by remember { mutableStateOf("sick") }
        var startDate by remember { mutableStateOf(currentTodayAbsence()) }
        var endDate by remember { mutableStateOf(currentTodayAbsence()) }
        var note by remember { mutableStateOf("") }
        var saving by remember { mutableStateOf(false) }

        Div({
            onClick { showCreateModal = false }
            style {
                property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
                property("background", "rgba(108, 60, 225, 0.25)"); property("display", "flex")
                property("align-items", "center"); property("justify-content", "center")
                property("backdrop-filter", "blur(4px)")
            }
        }) {
            Div({
                onClick { it.stopPropagation() }
                style {
                    property("background", "var(--rp-surface)"); property("border-radius", "28px")
                    property("padding", "2rem 2.25rem"); property("width", "440px"); property("max-width", "90vw")
                }
            }) {
                H3({ style { property("margin", "0 0 0.25rem 0"); property("font-size", "1.2rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                    Text("Ny frånvaro")
                }
                P({ style { property("margin", "0 0 1.5rem 0"); property("color", "var(--rp-muted)"); property("font-size", "0.9rem") } }) {
                    Text("Anmäl frånvaro till din chef")
                }

                // Type selector
                Div({ style { property("margin-bottom", "1.25rem") } }) {
                    Span({ style { absenceLabel() } }) { Text("Typ") }
                    Div({ style { property("display", "flex"); property("gap", "0.5rem"); property("flex-wrap", "wrap") } }) {
                        ABSENCE_TYPES.forEach { (value, label) ->
                            val active = selectedType == value
                            Button({
                                onClick { selectedType = value }
                                style {
                                    property("padding", "0.5rem 1rem"); property("border-radius", "50px"); property("border", "none")
                                    property("font-weight", "700"); property("font-size", "0.85rem"); property("cursor", "pointer")
                                    if (active) { property("background", "var(--rp-pine)"); property("color", "white") }
                                    else { property("background", "var(--rp-overlay)"); property("color", "var(--rp-subtle)") }
                                }
                            }) { Text(label) }
                        }
                    }
                }

                // Date inputs
                Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "1rem"); property("margin-bottom", "1.25rem") } }) {
                    Div {
                        Span({ style { absenceLabel() } }) { Text("Från") }
                        Input(InputType.Date) { value(startDate); onInput { startDate = it.value }; style { absenceInput() } }
                    }
                    Div {
                        Span({ style { absenceLabel() } }) { Text("Till") }
                        Input(InputType.Date) { value(endDate); onInput { endDate = it.value }; style { absenceInput() } }
                    }
                }

                // Note
                Div({ style { property("margin-bottom", "1.75rem") } }) {
                    Span({ style { absenceLabel() } }) { Text("Anteckning (valfritt)") }
                    Input(InputType.Text) {
                        value(note); onInput { note = it.value }
                        attr("placeholder", "T.ex. läkarbesök kl 10")
                        style { absenceInput() }
                    }
                }

                // Buttons
                Div({ style { property("display", "flex"); property("gap", "0.75rem"); property("justify-content", "flex-end") } }) {
                    Button({
                        onClick { showCreateModal = false }
                        style { property("padding", "0.75rem 1.5rem"); property("border", "2px solid var(--rp-pine)"); property("background", "transparent"); property("color", "var(--rp-pine)"); property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.95rem"); property("cursor", "pointer") }
                    }) { Text("Avbryt") }
                    Button({
                        onClick {
                            saving = true
                            absenceScope.launch {
                                absenceRepo.createAbsence(workplaceId, CreateAbsenceRequest(
                                    type = selectedType,
                                    startDate = startDate,
                                    endDate = endDate,
                                    note = note.ifBlank { null }
                                )).onSuccess {
                                    feedbackMsg = "\u2713 Frånvaro anmäld"
                                    showCreateModal = false
                                    loadAbsences()
                                }.onFailure {
                                    feedbackMsg = "Kunde inte anmäla: ${it.message}"
                                }
                                saving = false
                            }
                        }
                        if (saving) disabled()
                        style {
                            property("padding", "0.75rem 1.5rem"); property("border", "none")
                            property("background", "linear-gradient(135deg, var(--rp-pine), var(--rp-foam))"); property("color", "white")
                            property("border-radius", "50px"); property("font-weight", "700"); property("font-size", "0.95rem"); property("cursor", "pointer")
                            if (saving) property("opacity", "0.7")
                        }
                    }) { Text(if (saving) "Sparar\u2026" else "Skicka") }
                }
            }
        }
    }
}

private fun currentTodayAbsence(): String {
    val d = kotlin.js.Date()
    return "${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}-${d.getDate().toString().padStart(2,'0')}"
}

private fun org.jetbrains.compose.web.css.StyleScope.absenceLabel() {
    property("display", "block"); property("font-weight", "700"); property("font-size", "0.75rem"); property("color", "var(--rp-subtle)")
    property("text-transform", "uppercase"); property("letter-spacing", "0.5px"); property("margin-bottom", "0.4rem")
}

private fun org.jetbrains.compose.web.css.StyleScope.absenceInput() {
    property("width", "100%"); property("padding", "0.7rem 1rem"); property("border", "2px solid var(--rp-hl-med)")
    property("border-radius", "14px"); property("font-size", "1rem"); property("font-weight", "600")
    property("outline", "none"); property("box-sizing", "border-box")
}
